//
// File: ShellComponentIframe.tsx
// Description: Shell Component for loading and rendering iframed plugins
// Copyright: Broadlume 2024
//

import { Loader } from '@broadlume/willow-ui';
import * as React from 'react';
import { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiBaseRoot } from '../api/ApiRoutes';
import { AuthContextType, useAuth } from '../auth/AuthProvider';
import { IShellPluginConfig } from './IShellPluginConfig';
import { ShellIframeUrlHelper } from './ShellIframeUrlHelper';

/**
 * The props for the ShellComponentIframe
 */

export interface ShellIframeProps {
  /**
   * The plugin config object for the shell plugin (should be an iframe type)
   */
  config: IShellPluginConfig;
  /**
   * Optional style classname for the iframe element
   */
  className?: string;
  /**
   * Optional id of the iframe element
   */
  id?: string;
  /**
   * Optional callback to provide a custom error component to replace the plugin component
   * when an error occurs while loading the plugin
   * @returns A custom error component
   */
  renderError?(): React.ReactElement;
  /**
   * Optional callback to provide a custom placeholder component to replace the plugin component
   * while the plugin is loading or if no plugin is provided
   * @returns A custom placeholder component
   */
  renderPlaceholder?(): React.ReactElement;
  /*
   * Optional callback to handle session errors
   */
  sessionError?(): void;
}

/**
 * Create a component displaying an iframe. The plugin config
 * is used to determine the src of the iframe.
 */
export const ShellComponentIframe = (props: ShellIframeProps) => {
  const location = useLocation();
  const Navigate = useNavigate();
  const [useIframeRouter, setUseIframeRouter] = React.useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  //We use state to keep track of whether the src is valid or not

  const [src, setSrc] = React.useState('');
  const [loaded, setLoaded] = React.useState(false);
  const { user } = useAuth() as AuthContextType;
  const appRoute = props.config.shellRoute as string;

  const iframeProps = {
    className: props.className,
    id: props.id,
    allowFullScreen: true,
    frameBorder: 0,

    style: { width: '100%', height: '100%' },
  };

  //This validates that the initial src is valid
  const validateSrc = async () => {
    const newSrc = props.config['src'] + location.pathname.split(appRoute)[1];
    if (newSrc) {
      try {
        //We use a HEAD request with no-cors to check if the src is valid
        const valid = await fetch(newSrc, {
          method: 'HEAD',
          mode: 'no-cors',
        });
        //If the request is successful, we set the src to true which will render the iframe and add the event listener
        if (valid) {
          setSrc(newSrc);
          window.addEventListener('message', handler);
        }
      } catch (error) {
        console.error(`Error validating src: ${error}`);
      }
      setLoaded(true);
    }
  };

  const popStateHandler = (event: any) => {
    if (useIframeRouter == true && event.target.location.href.indexOf(appRoute) > -1) {
      const newRoute = location.pathname.split(appRoute)[1];
      event.preventDefault();
      iframeRef?.current?.contentWindow?.postMessage(
        { type: 'navigate', href: newRoute },
        { targetOrigin: '*' },
      );
    } else {
      window.location.href = event.target.location.href;
    }
  };
  //window.addEventListener('popstate',popStateHandler);
  //Our message handler for the iframe
  const handler = (ev: MessageEvent<{ type: string; message: string }>) => {
    //Only interested if coming from our iframe origin
    if (new URL(ev.origin).origin === new URL(props.config['src']).origin) {
      //Get the href from the ev.data
      const msgData: {
        type?:
          | 'update-address-bar'
          | 'navigate'
          | 'unauthorized'
          | 'unauthorised'
          | 'get-auth-token'
          | 'use-iframe-router';
        href?: string;
        newTab?: false;
      } = ev.data as object;

      //If the type is unauthorized, we call the sessionError prop
      if (msgData['type'] === 'unauthorized' || msgData['type'] === 'unauthorised') {
        props.sessionError && props.sessionError();
      }
      if (msgData['type'] == 'get-auth-token') {
        //If the type is get-auth-token, we send the idToken and the api base route to the iframe
        ev.source?.postMessage(
          { type: 'auth-token', token: user?.idToken, apiBaseRoute: ApiBaseRoot },
          { targetOrigin: '*' },
        );
      }
      if (msgData['type'] == 'use-iframe-router') {
        setUseIframeRouter(true);
      }
      if (msgData['type'] == 'navigate' && msgData['href']) {
        const newRoute = ShellIframeUrlHelper(
          location.pathname.split(appRoute)[1],
          msgData['href'],
        );
        const newURL = [location.pathname.split(appRoute)[0], appRoute, newRoute].join('');

        //Are we opening a new tab or not
        if (msgData['newTab']) {
          window.open(newURL, '_blank');
        } else {
          //We just want update the iframe src and the page url
          setSrc(props.config['src'] + newRoute);

          Navigate(
            newURL,
            { replace: false }, //Setting this to true or false will determine if the browser history is updated
          );
        }
      }
      if (msgData['type'] == 'update-address-bar' && msgData['href']) {
        const newRoute = ShellIframeUrlHelper(
          location.pathname.split(appRoute)[1],
          msgData['href'],
        );
        const newURL = [location.pathname.split(appRoute)[0], appRoute, newRoute].join('');

        //push the address state
        window.history.pushState(
          null, // State object (can be null)
          '', // Title (can be an empty string)
          newURL, // New URL
        );
      }
    }
  };
  React.useEffect(() => {
    window.addEventListener('popstate', popStateHandler);
    return () => {
      window.removeEventListener('popstate', popStateHandler);
    };
  }, [useIframeRouter]);

  //This takes affect on mount and when the location changes
  React.useEffect(() => {
    //Set the loaded state to false and validate the src
    setLoaded(false);
    validateSrc();
    // Remove any event listener on cleanup
    return () => {
      window.removeEventListener('message', handler);
    };
  }, [props.config['src'], location]);

  return (
    <>
      {!loaded ? (
        <div>
          {props.renderPlaceholder ? (
            props.renderPlaceholder()
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Loader />
            </div>
          )}
        </div>
      ) : src ? (
        <iframe ref={iframeRef} key={src} src={src} {...iframeProps} />
      ) : (
        <div>{props.renderError ? props.renderError() : <div>There was an error</div>}</div>
      )}
    </>
  );
};
